import { jsx, type JsxChild } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import type { ObservableValue } from '@amazon/vinyl-observable'
import { Modal } from '@/components/Modal'
import { openModal, type OpenModalHandle } from '@/components/modalLayer'
import { RichText } from '@/components/RichText'

export interface SystemModalOptions {
    readonly title: ObservableValue<string>
    readonly body?: ObservableValue<string>
    /** Body copy carrying inline links (`<a href>`); sanitized, and its links open in a new tab. */
    readonly bodyHtml?: ObservableValue<string>
    /** Markup (artwork, avatar, metadata) rendered between the title and the body copy. */
    readonly content?: JsxChild
    readonly primary?: { label: ObservableValue<string>; run: () => void }
    readonly secondary?: { label: ObservableValue<string>; run: () => void }
    /** Secondary action that opens `href` in a new tab and then closes the modal. */
    readonly externalLink?: {
        label: ObservableValue<string>
        href: string
        /** Runs when the link is pressed, before the modal closes. */
        onPress?: () => void
    }
    /** GH's modal content name: sent as the opening's content view and on the close button and backdrop's `selectDismiss`. */
    readonly contentName?: string
    /** True when the caller sends its own content view (with a ref marker or entities). */
    readonly ownContentView?: boolean
    readonly className?: string
    readonly dismissible?: boolean
    /** Runs when the user closes a dismissible modal without an action: Escape, the backdrop, the close button or a navigation. */
    readonly onDismiss?: () => void
    /** Runs once when the modal leaves the stack by any path. */
    readonly onClose?: () => void
}

/**
 * Shared layout for an informational / friction-style modal.
 * Mirrors GH's Default* modal shape — title + body + 1-2 actions —
 * routed through the app modal layer so any surface can pop one
 * without owning a portal.
 */
export function openSystemModal(options: SystemModalOptions): OpenModalHandle {
    return openModal({
        ...(options.onClose ? { onClose: options.onClose } : null),
        render: (close) => {
            const visible = data(true)
            let closing = false
            const closeWithFade = () => {
                closing = true
                visible.value = false
                queueMicrotask(close)
            }
            const dismiss = () => {
                if (closing) return
                options.onDismiss?.()
                closeWithFade()
            }

            const primaryRun = options.primary?.run ?? closeWithFade
            const dismissible = options.dismissible !== false

            return (
                <Modal
                    className={`systemModal ${options.className ?? ''}`}
                    visible={visible}
                    dismissible={dismissible}
                    {...(options.contentName ? { contentName: options.contentName } : null)}
                    ownContentView={options.ownContentView ?? false}
                    onClose={dismissible ? dismiss : () => undefined}
                >
                    <h2>{options.title}</h2>
                    {options.content ?? null}
                    {options.bodyHtml ? (
                        <RichText
                            className="systemModalBody"
                            html={options.bodyHtml}
                            dropEmptyParagraphs
                        />
                    ) : options.body ? (
                        <p>{options.body}</p>
                    ) : null}
                    <div
                        className={`systemModalActions ${options.externalLink && options.secondary ? 'threeActions' : ''}`}
                    >
                        {options.externalLink ? (
                            <a
                                className="textButton secondary"
                                href={options.externalLink.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onclick={() => {
                                    options.externalLink?.onPress?.()
                                    closeWithFade()
                                }}
                            >
                                {options.externalLink.label}
                            </a>
                        ) : null}
                        {options.secondary ? (
                            <button
                                className="textButton secondary"
                                onclick={() => {
                                    options.secondary?.run()
                                    closeWithFade()
                                }}
                            >
                                {options.secondary.label}
                            </button>
                        ) : null}
                        <button
                            className="button primary systemModalPrimary"
                            autofocus={true}
                            onclick={() => {
                                primaryRun()
                                closeWithFade()
                            }}
                        >
                            {options.primary?.label ?? data('OK')}
                        </button>
                    </div>
                </Modal>
            )
        },
    })
}
